import * as React from 'react';

import { getDisplayName } from 'components/hoc';
import debounce from 'helper/debounce';
import { error } from 'helper/debug';
import { SanitizeFunctionType } from 'helper/sanitizer';

export type ValidationProxyProps = {
	onValidationDone: (error?: string, message?: string) => void;
	value: string | number | null;
	validateOptions?: Object;
	sanitizers?: Array<SanitizeFunctionType>;
	onChange: (value: number | string) => void;
};

/*
 * Type Definition for Validation Higher Order Component.
 */
type ValidationProxyState = {
	/*
	 * References to an error provided by the validate promise.
	 * This can be a "silent" error which does not trigger an an invalidation on the underlying input, but will be
	 * handed to the parent component.
	 *
	 * @type string
	 */
	error: string | null;

	/*
	 * Error Message handed to the input field as an invalidation error message.
	 *
	 * @type string
	 */
	message: string | null;

	/*
	 * Validations are usually debounced. While this is ongoing, the underlying input should have a warning state so the
	 * user is aware of the ongoing validation.
	 *
	 * @type boolean
	 */
	isValidating: boolean;

	/*
	 * A given default value (provided as a prop) should be validated initially. Afterwards values will be validated
	 * onChange, so while bubbling up, not down.
	 *
	 * @type boolean
	 */
	hasRendered: boolean;
};

/*
 * ValidationError.
 */
export type ValidationError = {
	type: string;
	message?: string;
};

export type ValidateOptionsType = {
	[key: string]: string | number | boolean;
};

export type ValidationFactoryFunctionReturnType = Promise<ValidationError | undefined | void>;

/*
 * The Validation Factory Function should return a promise which is handled by the HOC.
 */
export type ValidationFactoryFunction = (
	value: string,
	validateOptions?: ValidateOptionsType
) => ValidationFactoryFunctionReturnType;

export type OptionsType = {
	shouldValidateInitial?: boolean;
};

/*
 * Decorates an Input Component with a validation layer.
 *
 * @param {React$Component} WrappedComponent
 * @param {Function} validate
 * @return {Component} Decorated Component
 */
export default function withValidation<P extends object>(
	WrappedComponent: React.ComponentType<P>,
	validate: ValidationFactoryFunction,
	sanitizers: SanitizeFunctionType[] = [],
	options?: OptionsType
) {
	return class extends React.Component<P & ValidationProxyProps, ValidationProxyState> {
		public static displayName = `WithValidation(${getDisplayName(WrappedComponent)})`;

		public static defaultProps = {
			value: '',
		};

		public state: ValidationProxyState = {
			error: null,
			message: null,
			isValidating: true,
			hasRendered: false,
		};

		/*
		 * Validates a value against a provided validator. Since validation will usually be debounced/delayed,
		 * the validation needs to be done asynchroniously (Promise based) and does not return anything.
		 */
		public validate = debounce(async (value: string) => {
			try {
				await validate(value, this.props.validateOptions as any);
				this.setState({
					error: null,
					message: null,
				});
			} catch (exception) {
				if (exception && exception.type) {
					this.setState({
						error: exception.type,
						message: exception.message || '',
					});
				}
			} finally {
				this.setState({
					isValidating: false,
				});

				this.props.onValidationDone(this.state.error!, this.state.message!);
			}
		}, 700);

		public componentDidMount() {
			if (options && options.shouldValidateInitial === true) {
				this.handleInitialValidation();
			}
		}

		/*
		 * Initially the provided value will be null but as soon as the Redux store contains a default size, a
		 * reconciliation will be triggered *with* a value. The first, and only the first, time this happens, the value
		 * needs to be validated. After the first time, a new value will be provided by the input's change event, so
		 * it's being validated on the way up, not down.
		 *
		 * @param {ValidationProxyProps} nextProps
		 * @returns void
		 */
		public componentDidUpdate(prevProps: ValidationProxyProps): void {
			const prevValue = prevProps.value;
			const currentValue = this.props.value;
			if (!prevValue && currentValue !== prevValue && !this.state.hasRendered) {
				this.setState({
					hasRendered: true,
				});

				this.validate(currentValue);
			}
		}

		public handleInitialValidation = (): void => {
			this.setState({
				isValidating: true,
			});

			let value: string | number = (this.props.value as any) || '';

			sanitizers.forEach((sanitize: Function) => {
				try {
					value = sanitize(value);
				} catch (sanitizeException) {
					error(
						`withValidation(...)#sanitize(...): Could not sanitize value ${value} with ${typeof sanitize}: ${sanitizeException}`
					);
				}
			});

			this.props.onChange(value);
			this.validate(value);
		};

		/*
		 * Callback Handler for onChange Event.
		 * @returns void
		 */

		public handleInputChange = (ev: Event): void => {
			this.setState({
				isValidating: true,
			});

			const target = ev.target;

			if (target instanceof HTMLInputElement) {
				let value: string | number = target.value || '';

				sanitizers.forEach((sanitize: Function) => {
					try {
						value = sanitize(value);
					} catch (sanitizeException) {
						error(
							`withValidation(...)#sanitize(...): Could not sanitize value ${value} with ${typeof sanitize}: ${sanitizeException}`
						);
					}
				});

				this.props.onChange(value);
				this.validate(value);
			}
		};

		/*
		 * Returns the input's UI State based on the actual component state.
		 * @returns "error" | "warning" | null
		 */
		public getInputState(): 'error' | 'warning' | null {
			if (this.state.error && !this.state.message) {
				return 'warning';
			}

			if (this.state.message) {
				return 'error';
			}

			return null;
		}

		public render() {
			const { message, isValidating } = this.state;

			return (
				<WrappedComponent
					{...this.props}
					onChange={this.handleInputChange}
					message={message}
					isValidating={isValidating}
					state={this.getInputState()}
				/>
			);
		}
	};
}
